import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { SensorsActions } from '@states/sensors/sensors.action-type';
import { SensorsService } from 'src/app/services/sensors.service';
import { catchError, map, mergeMap, of, share, switchMap } from 'rxjs';
import { SharedActions } from '@states/shared/shared.action-types';
import { LocationActions } from '@states/location/location.action-types';
import { UtilsService } from 'src/app/edge/utils.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable()
export class SensorsEffects {
  public loadVendorSensors$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SensorsActions.loadVendorSensors),
      mergeMap(({ vendor }) =>
        this.sensorsService.getAllSensorsForVendor(vendor).pipe(
          map((sensors) => SensorsActions.loadVendorSensorsSuccess({ vendor, sensors })),
          catchError((err) => of(SensorsActions.loadVendorSensorsFail({ vendor, error: this.utilsService.errMessage(err) }))),
        ),
      ),
    ),
  );

  public loadSensorById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SensorsActions.loadSensorById),
      switchMap(({ sensorId }) => [sensorId]),
      switchMap((sensorId) => {
        return this.sensorsService.getSensorById(sensorId).pipe(
          map((sensor) => SensorsActions.loadSensorByIdSuccess({ sensor })),
          catchError((err) => of(SensorsActions.loadSensorByIdFail({ sensorId, error: this.utilsService.errMessage(err) }))),
        );
      }),
    ),
  );

  public upsertSensor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SensorsActions.upsertSensor),
      switchMap(({ sensor }) =>
        this.sensorsService.upsertSensor(sensor).pipe(
          switchMap(() => [
            SensorsActions.upsertSensorSuccess(),
            SharedActions.showMessage({ success: this.translateService.instant('TOASTS.SENSOR_SAVE_SUCCESS') }),
            LocationActions.GetSensors(),
          ]),
          catchError((err) => of(
            SensorsActions.upsertSensorFail({ error: this.utilsService.errMessage(err) }),
          )),
        ),
      ),
    ),
  );

  public createSensor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SensorsActions.createSensor),
      switchMap(({ sensor }) =>
        this.sensorsService.createSensor(sensor).pipe(
          switchMap(() => [
            SensorsActions.createSensorSuccess(),
            SharedActions.showMessage({ success: this.translateService.instant('TOASTS.SENSOR_SAVE_SUCCESS') }),
            LocationActions.GetSensors(),
          ]),
          catchError((err) => of(
            SensorsActions.createSensorFail({ error: this.utilsService.errMessage(err) }),
          )),
        ),
      ),
    ),
  );

  public deleteSensor$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SensorsActions.deleteSensors),
      switchMap(({ sensorId }) => [sensorId]),
      switchMap((sensorId) => {
        return this.sensorsService.removeSensor(sensorId).pipe(
          map(() => SensorsActions.deleteSensorsSuccess()),
          catchError((err) => of(SensorsActions.deleteSensorsFail({ error: this.utilsService.errMessage(err) }))),
        );
      }),
    ),
  );

  public pressSaveSensor$ = createEffect(() => this.actions$.pipe(ofType(SensorsActions.pressSaveSensor), share()), {
    dispatch: false,
    useEffectsErrorHandler: false,
  });

  public pressCreateSensor$ = createEffect(() => this.actions$.pipe(ofType(SensorsActions.pressCreateSensor), share()), {
    dispatch: false,
    useEffectsErrorHandler: false,
  });

  constructor(
    private actions$: Actions,
    private sensorsService: SensorsService,
    private utilsService: UtilsService,
    private translateService: TranslateService,) { }
}


